import { FC, useMemo } from 'react';
import useImage from 'use-image';
import { Image, Layer, Stage } from 'react-konva';
import { DefaultCartModelDisplay } from '../CartModelDisplay';
import CartModelDisplayLoading from '../CartModelDisplayLoading/CartModelDisplayLoading';
import useCartModelDisplay from '../../../hooks/useCartModelDisplay';
import { Wheels14InLifted, Wheels14In } from '../../../data/products/Carts/XSeriesProducts/X6/X6Products/Wheels';

interface X6ModelProps {
  defaults?: DefaultCartModelDisplay,
  width?: number,
  height?: number
}

const X6Model: FC<X6ModelProps> = ({
  defaults,
  width = 1000,
  height = 1000
}) => {
  const { chassisType, bodyColor, liftKit, wheel, wheelSize, frontSeat } = useCartModelDisplay();

  const defaultChassisImage = chassisType?.liftKitOptions[1].configuratorImg ?? '';
  const chassisImageUrl = useMemo<String>(() => {
    let chassisImageUrl: String = '';
    if (defaults) {
      chassisImageUrl = defaults.liftKit?.configuratorImg ?? '';
    } else {
      chassisImageUrl = liftKit?.configuratorImg ?? defaultChassisImage;
    }
    return chassisImageUrl;
  }, [defaults, liftKit, defaultChassisImage]);

  const liftKitSku = liftKit?.sku;
  const wheelImageUrl = useMemo<String>(() => {
    let wheelImageUrl: String = '';
    if (defaults) {
      wheelImageUrl = defaults.wheel?.configuratorImg ?? '';
    } else {
      if (wheel?.configuratorImg) {
        wheelImageUrl = wheel?.configuratorImg;
      } else {
        wheelImageUrl =
          liftKitSku === 'L'
            ? Wheels14InLifted[0].configuratorImg ?? ''
            : Wheels14In[0].configuratorImg ?? ''
      }
    }
    return wheelImageUrl;
  }, [defaults, wheel, liftKitSku]);

  const seatsImageUrl = useMemo<String>(() => {
    let seatsImageUrl: String = '';
    const matchingSeatOption = chassisType?.frontSeatOptions.find((seat) => seat.refId === bodyColor?.refId);
    if (defaults) {
      seatsImageUrl = defaults.frontSeat?.configuratorImg ?? '';
    } else {
      if (frontSeat?.configuratorImg) {
        seatsImageUrl = frontSeat?.configuratorImg;
      } else {
        seatsImageUrl =
          matchingSeatOption?.configuratorImg ??
          chassisType?.frontSeatOptions[0].configuratorImg ?? '';
      }
    }
    return seatsImageUrl;
  }, [defaults, chassisType, frontSeat, bodyColor]);

  const defaultBodyColorImage = chassisType?.bodyColorOptions[0].configuratorImg ?? '';
  const bodyColorImageUrl = useMemo<String>(() => {
    let bodyColorImageUrl: String = '';
    if (defaults) {
      bodyColorImageUrl = defaults.bodyColor?.configuratorImg ?? '';
    } else {
      bodyColorImageUrl = bodyColor?.configuratorImg ?? defaultBodyColorImage;
    }
    return bodyColorImageUrl;
  }, [defaults, bodyColor, defaultBodyColorImage]);

  const tireImageUrl = useMemo<String>(() => {
    let tireImageUrl: String = '';
    if (wheelSize === -1 || wheelSize === undefined) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/ed3df485-d11e-4002-e2a9-c90d59868f00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/8536fcf3-b443-4105-cec3-b2d41380ee00'
    } else if (wheelSize === 12) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/cbcd11b3-96ff-4d78-e014-a2ce89fbbb00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/fd08d3d7-daec-47e9-6b44-83df4fae0300'

    } else if (wheelSize === 14) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/ed3df485-d11e-4002-e2a9-c90d59868f00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/8536fcf3-b443-4105-cec3-b2d41380ee00'

    } else if (wheelSize === 15) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/c4109ae9-8877-4de9-acab-21b257c88100'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/dc40672f-d226-4fcd-d095-63d2f4c01c00'
    }
    return tireImageUrl;
  }, [defaults, liftKitSku, wheelSize]);

  const fenderFlaresImageUrl = useMemo<String>(() => {
    let fenderFlaresImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/b7dd3e14-b660-4c91-dfb5-4524d7708500'
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
    return fenderFlaresImageUrl;
  }, [defaults, liftKitSku]);

  const brushGuardImageUrl = useMemo<String>(() => {
    let brushGuardImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/6946c9da-cf2c-4016-97fc-e5f73e261300'
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
    return brushGuardImageUrl;
  }, [defaults, liftKitSku]);

  const rearStrutsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f20831db-e52c-4de4-b72d-7a79074ce800";
  const sideMirrorsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/0ca340d3-4be7-401c-a675-0c98be6f4400";
  const steeringWheelImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/183b198f-9037-4e8f-0d14-af2e8b606900";
  const windshieldImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/a47ea13a-d925-4f57-8188-d56a0bdaf000";
  const topImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/42b50be7-a135-4e87-27f2-f893cd9e9500";
  const decalsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/10e6e7d1-9b0e-46a9-0298-75f5b61e7a00";
  const rearViewMirrorImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/2e854f30-0464-42f7-b2f5-ef316fc92700";
  const rearCameraImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/43ab067c-7c78-4818-41d6-9d925a352000";

  const frontBumperP1ImageUrl = useMemo(() => {
    let frontBumperP1ImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f9087cad-0ad5-42a5-263f-93959f1ce600'
    return frontBumperP1ImageUrl;
  }, [defaults, liftKitSku]);

  const frontBumperP2ImageUrl = useMemo(() => {
    let frontBumperP2ImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/d3ed0dc5-da41-4ac8-00cf-7883db24b500'
    return frontBumperP2ImageUrl;
  }, [liftKitSku]);


  const [chassisImage, chassisImageStatus] = useImage(`${chassisImageUrl}/w=${width}`);
  const [wheelImage, wheelImageStatus] = useImage(`${wheelImageUrl}/w=${width}`);
  const [seatsImage, seatsImageStatus] = useImage(`${seatsImageUrl}/w=${width}`);
  const [bodyColorImage, bodyColorImageStatus] = useImage(`${bodyColorImageUrl}/w=${width}`);
  const [tireImage, tireImageStatus] = useImage(`${tireImageUrl}/w=${width}`);
  const [fenderFlaresImage, fenderFlaresImageStatus] = useImage(`${fenderFlaresImageUrl}/w=${width}`);
  const [brushGuardImage, brushguardImageStatus] = useImage(`${brushGuardImageUrl}/w=${width}`)
  const [frontBumperPartOne, frontBumperPartOneImageStatus] = useImage(`${frontBumperP1ImageUrl}/w=${width}`);
  const [frontBumperPartTwo, frontBumperPartTwoImageStatus] = useImage(`${frontBumperP2ImageUrl}/w=${width}`);
  const [rearStrutsImage, rearStrutsImageStatus] = useImage(`${rearStrutsImageUrl}/w=${width}`);
  const [sideMirrorsImage, sideMirrorsImageStatus] = useImage(`${sideMirrorsImageUrl}/w=${width}`);
  const [steeringWheelImage, steeringWheelImageStatus] = useImage(`${steeringWheelImageUrl}/w=${width}`);
  const [windshieldImage, windshieldImageStatus] = useImage(`${windshieldImageUrl}/w=${width}`);
  const [topImage, topImageStatus] = useImage(`${topImageUrl}/w=${width}`);
  const [decalsImage, decalsImageStatus] = useImage(`${decalsImageUrl}/w=${width}`);
  const [rearViewMirrorImage, rearViewMirrorImageStatus] = useImage(`${rearViewMirrorImageUrl}/w=${width}`);
  const [rearCameraImage, rearCameraImageStatus] = useImage(`${rearCameraImageUrl}/w=${width}`);

  // Center Image on canvas
  const x = useMemo<number>(() => {
    let x: number = 0;
    if (chassisImage !== undefined) {

      /* after the image is loaded, you can get it's dimensions */
      const imageWidth = chassisImage?.width;

      /* 
        calculate the offsets so the image is centered inside
        the canvas
      */
      const xOffset = (width - imageWidth) / 2;
      x = xOffset;
    }
    return x;
  }, [width, chassisImage]);

  const y = useMemo<number>(() => {
    let y: number = 0;
    if (chassisImage !== undefined) {

      /* after the image is loaded, you can get it's dimensions */
      const imageHeight = chassisImage?.height;

      /* 
        calculate the offsets so the image is centered inside
        the canvas
      */
      const yOffset = (height - imageHeight) / 2;
      y = yOffset;
    }
    return y;
  }, [height, chassisImage]);

  const loading =
    chassisImageStatus === 'loading' ||
    wheelImageStatus === 'loading' ||
    tireImageStatus === 'loading' ||
    bodyColorImageStatus === 'loading' ||
    fenderFlaresImageStatus === 'loading' ||
    brushguardImageStatus === 'loading' ||
    seatsImageStatus === 'loading' ||
    steeringWheelImageStatus === 'loading' ||
    windshieldImageStatus === 'loading' ||
    topImageStatus === 'loading' ||
    decalsImageStatus === 'loading' ||
    rearCameraImageStatus === 'loading' ||
    rearViewMirrorImageStatus === 'loading' ||
    sideMirrorsImageStatus === 'loading' ||
    frontBumperPartOneImageStatus === 'loading' ||
    frontBumperPartTwoImageStatus === 'loading' ||
    rearStrutsImageStatus === 'loading';

  return (
    <CartModelDisplayLoading
      width={width}
      height={height}
      loading={loading}>
      <Stage width={width} height={height}>
        <Layer>
          <Image image={chassisImage} x={x} y={y} />
          <Image image={bodyColorImage} x={x} y={y} />
          <Image image={seatsImage} x={x} y={y} />
          <Image image={rearStrutsImage} x={x} y={y} />
          <Image image={sideMirrorsImage} x={x} y={y} />
          <Image image={frontBumperPartTwo} x={x} y={y} />
          <Image image={tireImage} x={x} y={y} />
          <Image image={wheelImage} x={x} y={y} />
          <Image image={steeringWheelImage} x={x} y={y} />
          <Image image={fenderFlaresImage} x={x} y={y} />
          <Image image={brushGuardImage} x={x} y={y} />
          <Image image={windshieldImage} x={x} y={y} />
          <Image image={rearViewMirrorImage} x={x} y={y} />
          <Image image={rearCameraImage} x={x} y={y} />
          <Image image={topImage} x={x} y={y} />
          <Image image={decalsImage} x={x} y={y} />
          <Image image={frontBumperPartOne} x={x} y={y} />
        </Layer>
      </Stage>
    </CartModelDisplayLoading>
  );
}

export default X6Model;
